import HBMessage from "@/components/HBMessage";
import { isCompanyType, isPersonalType, isVipByUser } from "@/constants/vip";
import useAppStore from "@/context/store";
import {
  checkBeforeMerge,
  checkCurrentTask,
  checkMergeResult,
  createGdAccuntAndMerge,
  getGdUserByCode,
  getGdUserByTel,
  mergeGdOrg,
  registerGd,
} from "@/services";
import { useAutoRetryFn } from "@/utils/autoRetryFnWrap";
import { checkLogin } from "@/utils/open";
import useReq from "@/utils/useReq";
import { useMemoizedFn, useMount } from "ahooks";
import { useState } from "react";
import { showErrorMessage } from "./Message";
import {
  ERROR_MAP,
  GdMergeStatus,
  ORGS_CLASS_MAP,
  TASK_STATUS,
} from "./shared";

export function useGdMerge({
  needAutoMerge = true,
  onMergeSuccess,
  onMergeFail,
}) {
  const [step, setStep] = useState<GdMergeStatus>();
  const [authGdUserData, setAuthGdUserData] = useState(null);

  const { user } = useAppStore((s) => ({
    user: s.user,
  }));

  const getTaskResult = useAutoRetryFn({
    fn: async (taskId: number) => {
      const result = await checkMergeResult({ taskId });
      if (result?.status === TASK_STATUS.success) {
        onMergeSuccess();
        return true;
      } else if (
        result?.status === TASK_STATUS.error ||
        result?.status === TASK_STATUS.rollback
      ) {
        HBMessage.error(result?.message || "升级失败，请稍后再试");
        onMergeFail();
        return true;
      }
    },
    retryInterval: 300,
  });

  const onMergeLoading = useMemoizedFn((taskId) => {
    setStep(GdMergeStatus.merging);
    getTaskResult(taskId);
  });

  const onAutoMerge = useMemoizedFn(async () => {
    // 自动根据用户手机号注册
    const result = await registerGd();

    // 手机号没有稿定账号，自动融合
    if (result.task?.task_id) {
      onMergeLoading(result.task?.task_id);
    }
  });

  const { data: sameTelGdUser } = useReq(async () => {
    if (user?.tel) {
      const userInfo = await getGdUserByTel();
      if (!userInfo?.gd_user && needAutoMerge) {
        onAutoMerge();
      } else {
        setStep(GdMergeStatus.init);
        return userInfo?.gd_user;
      }
    }

    setStep(GdMergeStatus.init);
  });

  const checkIsMerging = useMemoizedFn(async () => {
    const task = await checkCurrentTask();

    if (task?.task_id) {
      setStep(GdMergeStatus.merging);
      getTaskResult(task.task_id);
      return true;
    }
  });

  const [checkResult, setCheckResult] = useState<{
    result: boolean;
    temp_code: string;
    user_id: string;
    user_name: string;
    avatar: {
      url: string;
      width: number;
      height: number;
    };
    code?: string;
  }>();

  // 检查账号是否在中间状态
  const checkAccount = useMemoizedFn(async () => {
    const check = await checkBeforeMerge();

    // 合并中间态
    if (check.temp_code) {
      setStep(GdMergeStatus.interim);
      setCheckResult(check);
    }

    if (check.code === ERROR_MAP.HB_USER_IS_MERGE_ERROR) {
      // 合并完成
      onMergeSuccess();
    }

    return !check.result;
  });

  const onCreateAccount = useMemoizedFn(async () => {
    await checkLogin();

    const orgClass = isCompanyType(user)
      ? ORGS_CLASS_MAP.ORG
      : ORGS_CLASS_MAP.PERSONAL;

    const result: any = await createGdAccuntAndMerge({
      init_org_type: orgClass,
      hb_user_name: user?.username,
    });

    if (result?.error) {
      showErrorMessage(result);
    }

    onMergeLoading(result?.task_id);
  });

  const onMerge = useMemoizedFn(async (params) => {
    const result: any = await mergeGdOrg({
      hb_user_name: user?.username,
      ...params,
    });

    if (result?.error) {
      showErrorMessage(result);
      setStep(GdMergeStatus.init);
    }

    if (result?.task_id) {
      onMergeLoading(result.task_id);
    }
  });

  useMount(() => {
    checkIsMerging();
    checkAccount();
  });

  const onGaodingAuthCallback = useMemoizedFn(async (code, type = "AUTH") => {
    const gdUser: any = await getGdUserByCode({
      code,
      type: type || "AUTH",
    });

    // 检查状态
    if (await checkIsMerging()) {
      return false;
    }

    if (gdUser?.error) {
      showErrorMessage(gdUser);
      setStep(GdMergeStatus.init);
      return;
    }

    // 筛选超管的团队组织
    const orgs =
      gdUser?.orgs?.filter(
        (org) =>
          org?.is_super_admin &&
          org?.company?.org_class !== ORGS_CLASS_MAP.PERSONAL
      ) || [];

    const personalOrg = gdUser.orgs.find(
      (org) => org.company.org_class === ORGS_CLASS_MAP.PERSONAL
    );

    // 无会员或者个人会员，自动融合到个人组织，无个人组织则自动创建个人组织
    if ((!isVipByUser(user) && orgs.length === 0) || isPersonalType(user)) {
      await onMerge({
        user_id: gdUser?.user_id,
        is_init_org: !personalOrg,
        init_org_type: ORGS_CLASS_MAP.PERSONAL,
        ...(personalOrg ? { org_id: personalOrg.org_id } : null),
        code: gdUser?.code,
      });

      return;
    } else if (isCompanyType(user) && orgs.length === 0) {
      // 企业会员，没有团队组织，自动融合
      await onMerge({
        user_id: gdUser?.user_id,
        is_init_org: true,
        init_org_type: ORGS_CLASS_MAP.ORG,
        code: gdUser?.code,
      });
      return;
    }

    setStep(GdMergeStatus.selectOrg);
    setAuthGdUserData({
      orgs,
      user: gdUser,
    });
  });

  return {
    step,
    setStep,
    sameTelGdUser,
    checkResult,
    onMerge,
    authGdUserData,
    onGaodingAuthCallback,
    onCreateAccount,
  };
}
